<template>
  <HeaderComponent />
  <HeroComponent 
    image="https://images.pexels.com/photos/6614747/pexels-photo-6614747.jpeg?auto=compress&cs=tinysrgb&w=1260&h=750&dpr=2" 
    text="Our Amazing Projects" 
    subtitle="Discover the digital experiences we've crafted for our clients"
  />
  
  <!-- Projects section -->
  <div class="py-20 bg-gray-50">
    <div class="container mx-auto px-4">
      
      <!-- Advanced Filtering System -->
      <div v-if="!isLoading" class="mb-12 space-y-6" data-sal="slide-up" data-sal-delay="200">
        <!-- Search Bar -->
        <div class="max-w-2xl mx-auto">
          <div class="relative">
            <div class="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
              <svg class="h-5 w-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"></path>
              </svg>
            </div>
            <input
              v-model="searchQuery"
              type="text"
              class="block w-full pl-12 pr-4 py-4 border border-[#d1d5db] rounded-2xl bg-white shadow-lg focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 text-lg placeholder-gray-400"
              placeholder="Search projects by name, description, or technology..."
              @input="resetToFirstPage"
            >
            <div v-if="searchQuery" class="absolute inset-y-0 right-0 pr-4 flex items-center">
              <button
                @click="clearSearch"
                class="text-gray-400 hover:text-gray-600 transition-colors duration-200"
              >
                <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>

        <!-- Filter Controls -->
        <div class="flex flex-col lg:flex-row items-center justify-between space-y-4 lg:space-y-0 lg:space-x-6">
          <!-- Category Filter -->
          <div class="w-full lg:w-auto">
            <!-- Desktop Buttons -->
            <div class="hidden lg:flex flex-wrap justify-center lg:justify-start">
              <div class="bg-white rounded-full p-2 shadow-lg">
                <button
                  v-for="category in categories"
                  :key="category"
                  @click="setActiveFilter(category)"
                  :class="[
                    'px-6 py-3 !rounded-full font-medium transition-all duration-300 mx-1',
                    activeFilter === category 
                      ? 'bg-primary text-white shadow-lg transform scale-105' 
                      : 'text-gray-600 hover:text-primary hover:bg-gray-100'
                  ]"
                >
                  {{ category }}
                  <span v-if="category !== 'All'" class="ml-2 text-xs opacity-75">
                    ({{ getCategoryCount(category) }})
                  </span>
                </button>
              </div>
            </div>
            
            <!-- Mobile Dropdown -->
            <div class="lg:hidden relative">
              <select
                v-model="activeFilter"
                @change="setActiveFilter(activeFilter)"
                class="appearance-none bg-white border border-[#d1d5db] rounded-lg px-4 py-3 pr-10 text-gray-700 focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 cursor-pointer w-full shadow-lg"
              >
                <option value="All">All Categories ({{ projects.length }})</option>
                <option 
                  v-for="category in categories" 
                  :key="category" 
                  :value="category"
                >
                  {{ category }} ({{ getCategoryCount(category) }})
                </option>
              </select>
              <div class="absolute inset-y-0 right-0 flex items-center px-3 pointer-events-none">
                <svg class="w-5 h-5 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>
          </div>

          <!-- Sort Controls -->
          <div class="flex items-center space-x-4 w-full lg:w-auto justify-center lg:justify-end">
            <!-- Sort By -->
            <div class="relative">
              <select
                v-model="sortBy"
                @change="applySorting"
                class="appearance-none bg-white border border-[#d1d5db] rounded-lg px-4 py-2 pr-8 text-gray-700 focus:ring-2 focus:ring-primary focus:border-primary transition-all duration-300 cursor-pointer"
              >
                <option value="name">Sort by Name</option>
                <option value="year">Sort by Year</option>
                <option value="category">Sort by Category</option>
              </select>
              <div class="absolute inset-y-0 right-0 flex items-center px-2 pointer-events-none">
                <svg class="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 9l-7 7-7-7"></path>
                </svg>
              </div>
            </div>

            <!-- Sort Order -->
            <button
              @click="toggleSortOrder"
              :class="[
                'p-2 rounded-lg border border-[#d1d5db] bg-white hover:bg-gray-50 transition-all duration-300',
                'focus:ring-2 focus:ring-primary focus:border-primary'
              ]"
              :title="sortOrder === 'asc' ? 'Sort Ascending' : 'Sort Descending'"
            >
              <svg v-if="sortOrder === 'asc'" class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h6m4 0l4-4m0 0l4 4m-4-4v12"></path>
              </svg>
              <svg v-else class="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 4h13M3 8h9m-9 4h9m5-4v12m0 0l-4-4m4 4l4-4"></path>
              </svg>
            </button>
          </div>
        </div>

        <!-- Results Summary -->
        <div class="text-center">
          <p class="text-gray-600">
            <span class="font-semibold">{{ filteredProjects.length }}</span> 
            {{ filteredProjects.length === 1 ? 'project' : 'projects' }} found
            <span v-if="searchQuery || activeFilter !== 'All'">
              <span v-if="searchQuery"> for "<span class="font-medium text-primary">{{ searchQuery }}</span>"</span>
              <span v-if="activeFilter !== 'All'"> in <span class="font-medium text-primary">{{ activeFilter }}</span></span>
            </span>
          </p>
        </div>
      </div>
      
      <!-- Loading Animation -->
      <div v-if="isLoading" class="py-20">
        <LoadingRipple message="Loading amazing projects..." />
      </div>

      <!-- Projects Grid -->
      <div v-if="!isLoading && projects.length > 0" class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mb-12">
        <div
          v-for="project in paginatedProjects"
          :key="project.id"
          class="bg-white rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 overflow-hidden group"
        >
          <router-link :to="`/projects/${project.slug}`" class="block project-card-link">
            <!-- Project Image -->
            <div class="relative overflow-hidden h-48">
              <img 
                :src="project.image" 
                :alt="project.alt"
                class="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
              >
              <!-- Overlay -->
              <div class="absolute inset-0 bg-gradient-to-t from-primary/80 via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
              
              <!-- Category Badge -->
              <div class="absolute top-4 left-4">
                <span class="bg-white/90 backdrop-blur-sm text-primary px-3 py-1 rounded-full text-sm font-medium">
                  {{ project.category }}
                </span>
              </div>
              
              <!-- View Project Icon -->
              <div class="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                <div class="bg-white/20 backdrop-blur-sm rounded-full p-3">
                  <svg class="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
                    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"></path>
                  </svg>
                </div>
              </div>
            </div>
            
            <!-- Project Info -->
            <div class="p-6">
              <h3 class="text-xl font-bold font-heading text-gray-900 mb-2 group-hover:text-primary transition-colors duration-300">
                {{ project.name }}
              </h3>
              <p class="text-gray-600 text-sm leading-relaxed mb-4">
                {{ project.description && project.description.length > 120 
                    ? project.description.substring(0, 120) + '...' 
                    : project.description || 'No description available.' }}
              </p>
              
              <!-- Technologies Used -->
              <div class="flex flex-wrap gap-2 mb-4">
                <span 
                  v-for="tech in project.technologies" 
                  :key="tech"
                  class="bg-gray-100 text-gray-600 px-2 py-1 rounded-md text-xs font-medium"
                >
                  {{ tech }}
                </span>
              </div>
              
              <!-- Project Stats -->
              <div class="flex items-center justify-between text-sm text-gray-500">
                <span class="flex items-center">
                  <svg class="w-4 h-4 mr-1" fill="currentColor" viewBox="0 0 20 20">
                    <path fill-rule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clip-rule="evenodd"></path>
                  </svg>
                  {{ project.releaseDate || 'N/A' }}
                </span>
                <span class="text-primary font-medium no-underline">
                  View Project →
                </span>
              </div>
            </div>
          </router-link>
        </div>
      </div>
      
      <!-- Pagination -->
      <div v-if="!isLoading && totalPages > 1" class="flex flex-col items-center space-y-4" data-sal="slide-up" data-sal-delay="600">
        <!-- Page Info -->
        <div class="text-gray-600 text-sm">
          Showing {{ startItem }}-{{ endItem }} of {{ filteredProjects.length }} projects
        </div>
        
        <!-- Pagination Controls -->
        <div class="flex items-center space-x-2">
          <!-- Previous Button -->
          <button
            @click="goToPage(currentPage - 1)"
            :disabled="currentPage === 1"
            :class="[
              'flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300',
              currentPage === 1 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-gray-700 hover:bg-primary hover:text-white shadow-md hover:shadow-lg'
            ]"
          >
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 19l-7-7 7-7"></path>
            </svg>
            Previous
          </button>
          
          <!-- Page Numbers -->
          <div class="flex items-center space-x-1">
            <button
              v-for="page in visiblePages"
              :key="page"
              @click="goToPage(page)"
              :class="[
                'w-10 h-10 rounded-lg font-medium transition-all duration-300',
                page === currentPage 
                  ? 'bg-primary text-white shadow-lg transform scale-110' 
                  : 'bg-white text-gray-700 hover:bg-gray-100 shadow-md hover:shadow-lg'
              ]"
            >
              {{ page }}
            </button>
          </div>
          
          <!-- Next Button -->
          <button
            @click="goToPage(currentPage + 1)"
            :disabled="currentPage === totalPages"
            :class="[
              'flex items-center px-4 py-2 rounded-lg font-medium transition-all duration-300',
              currentPage === totalPages 
                ? 'bg-gray-100 text-gray-400 cursor-not-allowed' 
                : 'bg-white text-gray-700 hover:bg-primary hover:text-white shadow-md hover:shadow-lg'
            ]"
          >
            Next
            <svg class="w-4 h-4 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7"></path>
            </svg>
          </button>
        </div>
      </div>
    </div>
  </div>
  <!-- end Projects section -->
  
  <FooterComponent />
</template>
<script>
import HeaderComponent from '@/components/Header.vue'
import FooterComponent from '@/components/Footer.vue'
import HeroComponent from '@/components/Hero.vue'
import LoadingRipple from '@/components/LoadingRipple.vue'

export default {
  name: 'ProjectsView',
  components: {
    HeaderComponent,
    FooterComponent,
    HeroComponent,
    LoadingRipple
  },
  data() {
    return {
      activeFilter: 'All',
      currentPage: 1,
      projectsPerPage: 12,
      isLoading: true,
      searchQuery: '',
      sortBy: 'year',
      sortOrder: 'desc',
      projects: [],
      categories: []
    };
  },
  async created() {
    await this.loadProjectsIndex();
  },
  computed: {
    filteredProjects() {
      let filtered = this.projects;

      // Apply category filter
      if (this.activeFilter !== 'All') {
        filtered = filtered.filter(project => project.category === this.activeFilter);
      }

      // Apply search filter
      if (this.searchQuery.trim()) {
        const query = this.searchQuery.toLowerCase().trim();
        filtered = filtered.filter(project => 
          project.name.toLowerCase().includes(query) ||
          (project.description && project.description.toLowerCase().includes(query)) ||
          project.technologies.some(tech => tech.toLowerCase().includes(query)) ||
          project.category.toLowerCase().includes(query)
        );
      }

      // Apply sorting
      filtered = [...filtered].sort((a, b) => {
        let aValue, bValue;
        
        switch (this.sortBy) {
          case 'year':
            // For YYYY-MM format, we can sort directly as strings since YYYY-MM sorts chronologically
            aValue = a.releaseDate || '2020-01';
            bValue = b.releaseDate || '2020-01';
            break;
          case 'category':
            aValue = a.category.toLowerCase();
            bValue = b.category.toLowerCase();
            break;
          default: // name
            aValue = a.name.toLowerCase();
            bValue = b.name.toLowerCase();
        }

        if (this.sortOrder === 'asc') {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        } else {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        }
      });

      return filtered;
    },
    totalPages() {
      return Math.ceil(this.filteredProjects.length / this.projectsPerPage);
    },
    paginatedProjects() {
      const start = (this.currentPage - 1) * this.projectsPerPage;
      const end = start + this.projectsPerPage;
      return this.filteredProjects.slice(start, end);
    },
    visiblePages() {
      const pages = [];
      const total = this.totalPages;
      const current = this.currentPage;
      
      // Show max 5 page numbers
      let start = Math.max(1, current - 2);
      let end = Math.min(total, start + 4);
      
      // Adjust start if we're near the end
      if (end - start < 4) {
        start = Math.max(1, end - 4);
      }
      
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      return pages;
    },
    startItem() {
      return (this.currentPage - 1) * this.projectsPerPage + 1;
    },
    endItem() {
      return Math.min(this.currentPage * this.projectsPerPage, this.filteredProjects.length);
    }
  },
  methods: {
    async loadProjectsIndex() {
      try {
        this.isLoading = true;
        
        // Try to load from NocoDB first
        try {
          const nocoService = (await import('@/services/nocodb.js')).default;
          const data = await nocoService.getProjects();
          
          if (data && data.list && data.list.length > 0) {
            this.projects = data.list.map(project => ({
              ...project,
              image: project.image && project.image.length > 0
                ? `https://noco.drods.ch/${project.image[0].signedPath || project.image[0].path}`
                : 'https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?w=150&h=150&fit=crop&crop=face&auto=format',
              technologies: typeof project.technologies === 'string' 
                ? project.technologies.split(', ').map(tech => tech.trim())
                : project.technologies || []
            }));
            
            // Extract unique categories from projects
            const categorySet = new Set();
            this.projects.forEach(project => {
              if (project.category) {
                categorySet.add(project.category);
              }
            });
            this.categories = ['All', ...Array.from(categorySet)];
            return;
          }
        } catch (nocoError) {
          // Silently handle NocoDB connection issues
        }
        
        // Fallback to local JSON data
        try {
          const indexModule = await import('@/assets/projects/index.json');
          const data = indexModule.default;
          
          this.projects = data.projects;
          // Ensure "All" is only added once
          const filteredCategories = data.categories.filter(cat => cat !== 'All');
          this.categories = ['All', ...filteredCategories];
        } catch (jsonError) {
          // If no fallback data and no NocoDB data, ensure we at least have "All"
          if (this.categories.length === 0) {
            this.categories = ['All'];
          }
        }
        
      } catch (error) {
        // Silently handle errors
      } finally {
        // Simulate loading delay for better UX
        setTimeout(() => {
          this.isLoading = false;
          this.$nextTick(() => {
            if (typeof sal !== 'undefined') {
              sal({
                duration: 500,
                threshold: 0.1,
                once: true
              });
            }
          });
        }, 1500);
      }
    },
    setActiveFilter(category) {
      this.activeFilter = category;
      this.currentPage = 1; // Reset to first page when filtering
    },
    goToPage(page) {
      if (page >= 1 && page <= this.totalPages) {
        this.currentPage = page;
        // Scroll to top of projects section
        window.scrollTo({ top: 0, behavior: 'smooth' });
      }
    },
    clearSearch() {
      this.searchQuery = '';
      this.currentPage = 1;
    },
    resetToFirstPage() {
      this.currentPage = 1;
    },
    applySorting() {
      this.currentPage = 1;
    },
    toggleSortOrder() {
      this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc';
      this.currentPage = 1;
    },
    getCategoryCount(category) {
      return this.projects.filter(project => project.category === category).length;
    }
  }
}
</script>

<style scoped>
.project-card-link {
  text-decoration: none !important;
}

.project-card-link:hover {
  text-decoration: none !important;
}

.project-card-link * {
  text-decoration: none !important;
}
</style>